import { APP_ENV } from "#next/appEnv";
import { ErrorModal } from "#next/components/base/ErrorBoundary";
import Info from "@mui/icons-material/Info";
import { Grid, Typography } from "@mui/material";
import { TRPCClientErrorBase } from "@trpc/client";
import {
  InferQueryLikeData,
  InferQueryLikeInput,
  QueryLike,
  UseTRPCQueryOptions,
} from "@trpc/react-query/shared";
import { ReactElement, useState } from "react";
import LoadingContent from "#next/components/reusable/LoadingContent";

export type BiQueryProps<
  T extends QueryLike,
  T2 extends QueryLike,
  G extends InferQueryLikeInput<T>,
  G2 extends InferQueryLikeInput<T2>,
  O extends UseTRPCQueryOptions<any, any, any, any, any>,
  O2 extends UseTRPCQueryOptions<any, any, any, any, any>,
  P extends InferQueryLikeData<T>,
  P2 extends InferQueryLikeData<T2>,
> = {
  useQuery: [T, T2];
  input?: [G, G2];
  options?: [O, O2];
  loadingComponent?: React.ReactNode;
  render: (data: P, data2: P2) => ReactElement;
} & SharedProps;

export type SingleQueryProps<
  T extends QueryLike,
  G extends InferQueryLikeInput<T>,
  O extends UseTRPCQueryOptions<any, any, any, any, any>,
  P extends InferQueryLikeData<T>,
> = {
  useQuery: T;
  input?: G;
  options?: O;
  loadingComponent?: React.ReactNode;
  render: (data: P) => ReactElement;
} & SharedProps;

type SharedProps = {
  renderLoading?: () => ReactElement;
  renderError?: (error: TRPCClientErrorBase<any>) => ReactElement;
};

/**
 * This is our standard helper for rendering the output from a `useQuery`.
 * It should be used everywhere `useQuery` is used in isolation, because it provides
 * a consistent UI for loading and error messaging.
 * @param useQuery
 * @param render
 * @param input
 * @returns
 */
export function RenderQuery<
  T extends QueryLike,
  T2 extends QueryLike,
  G extends InferQueryLikeInput<T>,
  G2 extends InferQueryLikeInput<T2>,
  O extends UseTRPCQueryOptions<any, any, any, any, any>,
  O2 extends UseTRPCQueryOptions<any, any, any, any, any>,
  P extends InferQueryLikeData<T>,
  P2 extends InferQueryLikeData<T2>,
>({
  useQuery,
  input,
  render,
}: BiQueryProps<T, T2, G, G2, O, O2, P, P2>): JSX.Element;
export function RenderQuery<
  T extends QueryLike,
  G extends InferQueryLikeInput<T>,
  O extends UseTRPCQueryOptions<any, any, any, any, any>,
  P extends InferQueryLikeData<T>,
>({ useQuery, input, render }: SingleQueryProps<T, G, O, P>): JSX.Element;
export function RenderQuery<
  T extends QueryLike,
  T2 extends QueryLike,
  G extends InferQueryLikeInput<T>,
  G2 extends InferQueryLikeInput<T2>,
  O extends UseTRPCQueryOptions<any, any, any, any, any>,
  O2 extends UseTRPCQueryOptions<any, any, any, any, any>,
  P extends InferQueryLikeData<T>,
  P2 extends InferQueryLikeData<T2>,
>(
  props:
    | SingleQueryProps<T, G, O, P>
    | BiQueryProps<T, T2, G, G2, O, O2, P, P2>,
) {
  if (isBiQuery(props)) {
    const { useQuery, input, options, render, renderError, renderLoading } =
      props;
    const [query1, query2] = useQuery.map((q, i) => {
      const variables = input?.[i];
      const opts = options?.[i];
      return q.useQuery(variables, opts);
    });
    if (!query1 || !query2) {
      throw new Error("Should never happen - this check is to keep TS happy");
    }
    const failedQuery = query1.status === "error" ? query1 : query2;
    if (failedQuery && failedQuery.status === "error") {
      return (
        renderError?.(failedQuery.error) ?? (
          <RenderError error={failedQuery.error} />
        )
      );
    } else if (query1.status === "loading" || query2.status === "loading") {
      return (
        renderLoading?.() ??
        (props.loadingComponent ? props.loadingComponent : <LoadingContent />)
      );
    } else {
      return render(query1.data, query2.data);
    }
  } else {
    const { useQuery, input, render, renderError, renderLoading } = props;
    const query = useQuery.useQuery(input);
    switch (query.status) {
      case "error":
        return (
          renderError?.(query.error) ?? <RenderError error={query.error} />
        );
      case "loading":
        return (
          renderLoading?.() ??
          (props.loadingComponent ? props.loadingComponent : <LoadingContent />)
        );
      case "success":
        return render(query.data);
      default:
        const _exhaustivenessCheck: never = query;
        return _exhaustivenessCheck;
    }
  }
}

function isBiQuery<
  T extends QueryLike,
  T2 extends QueryLike,
  G extends InferQueryLikeInput<T>,
  G2 extends InferQueryLikeInput<T2>,
  O extends UseTRPCQueryOptions<any, any, any, any, any>,
  O2 extends UseTRPCQueryOptions<any, any, any, any, any>,
  P extends InferQueryLikeData<T>,
  P2 extends InferQueryLikeData<T2>,
>(
  props:
    | SingleQueryProps<T, G, O, P>
    | BiQueryProps<T, T2, G, G2, O, O2, P, P2>,
): props is BiQueryProps<T, T2, G, G2, O, O2, P, P2> {
  return Array.isArray(props.useQuery);
}

function RenderError({ error }: { error: TRPCClientErrorBase<any> | Error }) {
  const [showErrorDetailsModal, setShowErrorDetailsModal] =
    useState<boolean>(false);

  if (APP_ENV !== "production") {
    throw error;
  }
  console.error(error);
  return (
    <Grid
      container
      direction="column"
      sx={{ cursor: "pointer" }}
      onClick={() => setShowErrorDetailsModal(true)}
    >
      <Typography>Sorry, something went wrong.</Typography>
      <Typography variant="body2" sx={{ display: "flex", gap: "0.5em" }}>
        <Info fontSize={"small"} /> More Details
      </Typography>
      <ErrorModal
        error={error}
        open={showErrorDetailsModal}
        onClose={() => setShowErrorDetailsModal(false)}
      />
    </Grid>
  );
}
